/** Automatic local-disk incremental backup scheduler.

The engine (`runDiskBackup`) is incremental and safe on a timer. This module owns
*when* it runs: schedule, overlap guard, retries/backoff, and cleanup.

DURABLE CURSOR (THE WHOLE POINT)
--------------------------------
`runDiskBackup` advances the manifest cursor (`lastJournaledAt` /
`lastJournaledChangeId`) only after the journal append succeeds and the manifest
is written atomically on top. That cursor is the promise that "everything
committed before this point is on disk." `outbox.ts` reads it to refuse local
compaction that would delete outbox rows the disk has not verified.

BROWSER NOTE
------------
The scheduler runs while the engine is mounted at the authenticated app root.
A backgrounded browser tab may run a tick late; the monotonic manifest cursor
and gap-checked `journalSequencesOk` make a late tick harmless.
*/

import type { DiskBackupTarget } from './diskBackupTarget';
import { resolveDiskBackupTarget, type ResolvedTarget } from './resolveDiskBackupTarget';
import { runDiskBackup, type DiskBackupRunResult } from './localBackupEngine';

export const DEFAULT_AUTO_BACKUP_INTERVAL_MS = 60_000;
export const MAX_AUTO_BACKUP_INTERVAL_MS = 300_000;
export const INITIAL_AUTO_BACKUP_DELAY_MS = 1_000;

/** Registry token for the single app-wide scheduler mounted by the app root. */
export const AUTO_BACKUP_SCHEDULER_TOKEN = 'dnr-vyapar-auto-backup-scheduler';

// Re-exported so callers and tests import the scheduler's contract from one place.
export type { ResolvedTarget } from './resolveDiskBackupTarget';
export type { DiskBackupRunResult } from './localBackupEngine';

export interface BackupSchedulerOptions {
  /** Base delay between runs once the scheduler is armed. */
  intervalMs?: number;
  /** Upper bound the backoff doubles toward. */
  maxIntervalMs?: number;
  /** Double the interval after a failed run (bounded by `maxIntervalMs`). */
  backoffOnError?: boolean;
  /** Delay before the first run after `enable()`. */
  initialRunDelayMs?: number;
  onRun: (target: ResolvedTarget, now: string) => Promise<DiskBackupRunResult>;
  token?: string;
  /**
   * Test seam: how to resolve the backup target. Defaults to probing the real
   * platform (`resolveDiskBackupTarget`); a test injects an in-memory/durable
   * target so `enable()` is deterministic without a filesystem.
   */
  resolveTarget?: () => ResolvedTarget;
}

export type BackupSchedulerState = 'idle' | 'running' | 'stopped' | 'error';

export interface BackupSchedulerHandle {
  readonly target: ResolvedTarget | null;
  readonly state: BackupSchedulerState;
  readonly isRunning: boolean;
  /**
   * True only while the timer is armed: `enable()` found the folder ready and
   * scheduled a run. This is the fact the status panel shows as "On" — a
   * configured folder is NOT the same as a running scheduler, and reporting
   * "On" off the configuration alone is what made the badge contradict itself.
   */
  readonly armed: boolean;
  readonly lastRunAt: string | null;
  readonly lastError: string | null;
  enable(): Promise<ResolvedTarget | null>;
  stop(): void;
  runNow(): Promise<DiskBackupRunResult>;
}

const registry = new Map<string, BackupSchedulerHandle>();

function isBackupRunResult(v: unknown): v is DiskBackupRunResult {
  return typeof v === 'object' && v !== null && 'ok' in v && 'status' in v;
}

function defaultErrorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  return 'Automatic disk backup did not complete.';
}

function throwIfDuplicate(token: string | undefined): void {
  if (token && registry.has(token)) {
    throw new Error(`A backup scheduler for token "${token}" is already registered.`);
  }
}

export function createBackupScheduler(token: string, options: BackupSchedulerOptions): BackupSchedulerHandle {
  throwIfDuplicate(token);
  const handle = new BackupScheduler(token, options);
  // Only real tokens are tracked; an untokened scheduler is a private instance
  // (tests use these) and is never reachable through the registry.
  if (token) registry.set(token, handle);
  return handle;
}

export function getBackupScheduler(token: string): BackupSchedulerHandle | null {
  return registry.get(token) ?? null;
}

export function isBackupSchedulerAvailable(token: string): boolean {
  return !registry.has(token);
}

export function disposeBackupScheduler(token: string): boolean {
  const handle = registry.get(token);
  if (handle) {
    handle.stop();
    registry.delete(token);
  }
  return !registry.has(token);
}

/**
 * Get-or-create the single app-wide scheduler and enable it.
 *
 * Returns the resolved target only when the platform can back up somewhere
 * DURABLE AND a folder has been configured with accessible permission;
 * otherwise null, so the caller can show "not configured" instead of a
 * false "enabled". The scheduler is a process singleton keyed by
 * AUTO_BACKUP_SCHEDULER_TOKEN, so a second mount reuses the first rather than
 * racing two timers.
 *
 * CRITICAL: the scheduler does NOT start its timer if the target is not ready.
 * "Ready" means: a folder is configured AND `checkReady()` reports
 * 'ready' (permission granted). This prevents the timer from firing
 * `runDiskBackup` without a user gesture, which would call
 * `ensurePermission` → `requestPermission`, which browsers block from
 * non-gesture contexts. The timer starts only when a real write can succeed.
 */
export async function ensureAutoBackupScheduler(
  options: Omit<BackupSchedulerOptions, 'token'>,
): Promise<ResolvedTarget | null> {
  const existing = getBackupScheduler(AUTO_BACKUP_SCHEDULER_TOKEN);
  const handle = existing ?? createBackupScheduler(AUTO_BACKUP_SCHEDULER_TOKEN, options);
  const resolved = await handle.enable();
  if (!resolved || !resolved.durable) {
    // Non-durable (in-memory) target writes nowhere; do not leave a timer
    // ticking against it. If a caller already mounted a real one, leave that
    // registered scheduler as-is and only report the non-durable outcome.
    if (!existing) disposeBackupScheduler(AUTO_BACKUP_SCHEDULER_TOKEN);
    return null;
  }
  return resolved;
}

class BackupScheduler implements BackupSchedulerHandle {
  #token: string;
  #onRun: (target: ResolvedTarget, now: string) => Promise<DiskBackupRunResult>;
  #resolveTarget: () => ResolvedTarget;
  #intervalMs: number;
  #maxIntervalMs: number;
  #backoffOnError: boolean;
  #initialRunDelayMs: number;
  #enabled = false;
  #stopped = false;
  #running = false;
  #target: ResolvedTarget | null = null;
  #state: BackupSchedulerState = 'idle';
  #lastRunAt: string | null = null;
  #lastError: string | null = null;
  #timerHandle: ReturnType<typeof setTimeout> | null = null;
  #currentIntervalMs: number;

  constructor(token: string, options: BackupSchedulerOptions) {
    this.#token = token;
    this.#onRun = options.onRun;
    this.#resolveTarget = options.resolveTarget ?? resolveDiskBackupTarget;
    this.#intervalMs = options.intervalMs ?? DEFAULT_AUTO_BACKUP_INTERVAL_MS;
    this.#maxIntervalMs = options.maxIntervalMs ?? MAX_AUTO_BACKUP_INTERVAL_MS;
    this.#backoffOnError = options.backoffOnError ?? true;
    this.#initialRunDelayMs = options.initialRunDelayMs ?? INITIAL_AUTO_BACKUP_DELAY_MS;
    this.#currentIntervalMs = this.#intervalMs;
  }

  get target(): ResolvedTarget | null { return this.#target; }
  get state(): BackupSchedulerState { return this.#state; }
  get isRunning(): boolean { return this.#running; }
  get armed(): boolean { return this.#enabled && !this.#stopped; }
  get lastRunAt(): string | null { return this.#lastRunAt; }
  get lastError(): string | null { return this.#lastError; }

  private reset(): void {
    this.#state = 'idle';
    this.#lastRunAt = null;
    this.#lastError = null;
    this.#enabled = false;
    this.#stopped = false;
    this.#running = false;
    if (this.#timerHandle !== null) {
      globalThis.clearTimeout(this.#timerHandle);
      this.#timerHandle = null;
    }
    this.#currentIntervalMs = this.#intervalMs;
  }

  async enable(): Promise<ResolvedTarget | null> {
    if (this.#enabled) return this.#target;
    this.reset();
    const resolved = this.#resolveTarget();
    this.#target = resolved;
    // Check readiness BEFORE starting the timer. A target may be `durable`
    // (the platform supports writing to a real folder) but not yet
    // configured (no folder selected) or permission may be lost. Starting the
    // timer on a non-ready target would fire #tick() → runDiskBackup() without
    // a user gesture, triggering requestPermission which browsers block from
    // timer callbacks — silently failing every cycle.
    const readiness = await resolved.target.checkReady();
    if (readiness !== 'ready') {
      this.#target = null;
      return null;
    }
    this.#enabled = true;
    this.#timerHandle = globalThis.setTimeout(() => void this.#tick(), this.#initialRunDelayMs);
    return resolved;
  }

  stop(): void {
    this.#stopped = true;
    this.#enabled = false;
    if (this.#timerHandle !== null) {
      globalThis.clearTimeout(this.#timerHandle);
      this.#timerHandle = null;
    }
    this.#running = false;
  }


  async runNow(): Promise<DiskBackupRunResult> {
    if (this.#stopped || !this.#enabled || !this.#target) {
      return { ok: false, status: { enabled: false, supported: false, folderLabel: '', checkpointSeq: 0, nextSeq: 0, pendingSinceCheckpoint: 0, lastBackupAt: '', lastError: 'Automatic disk backup is not enabled.' } };
    }
    const now = new Date().toISOString();
    const result = await this.#onRun(this.#target, now);
    if (result.ok) {
      this.#lastRunAt = now;
      this.#lastError = null;
      this.#currentIntervalMs = this.#intervalMs;
    } else {
      const message = result.error ?? defaultErrorMessage(result.status?.lastError ?? result);
      this.#lastError = message;
      if (this.#backoffOnError) {
        this.#currentIntervalMs = Math.min(this.#maxIntervalMs, this.#currentIntervalMs * 2);
      }
    }
    return result;
  }

  async #tick(): Promise<void> {
    this.#timerHandle = null;
    if (this.#stopped || !this.#enabled) return;
    if (this.#running) {
      this.#scheduleNext();
      return;
    }
    this.#running = true;
    try {
      const now = new Date().toISOString();
      const result = await this.#onRun(this.#target!, now);
      if (result.ok) {
        this.#lastRunAt = now;
        this.#lastError = null;
        this.#currentIntervalMs = this.#intervalMs;
      } else {
        const message = result.error ?? defaultErrorMessage(result.status?.lastError ?? result);
        this.#lastError = message;
        if (this.#backoffOnError) {
          this.#currentIntervalMs = Math.min(this.#maxIntervalMs, this.#currentIntervalMs * 2);
        }
      }
    } catch (error) {
      const message = defaultErrorMessage(error);
      this.#lastError = message;
      if (this.#backoffOnError) {
        this.#currentIntervalMs = Math.min(this.#maxIntervalMs, this.#currentIntervalMs * 2);
      }
    } finally {
      this.#running = false;
      this.#scheduleNext();
    }
  }

  #scheduleNext(): void {
    if (this.#stopped || !this.#enabled) return;
    // Keep exactly one timer pending: a re-arm mid-flight must not stack a
    // second chain behind the first. Clearing before scheduling guarantees a
    // single active run loop that `stop()` can always cancel.
    if (this.#timerHandle !== null) {
      globalThis.clearTimeout(this.#timerHandle);
    }
    this.#timerHandle = globalThis.setTimeout(() => void this.#tick(), this.#currentIntervalMs);
  }
}
